import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  computeStandings,
  divisionStandings,
  groupStake,
  slotContext,
} from "../../shared/standings";
import { displayPercent, winProbabilityA } from "../../shared/winprob";
import {
  autoBanner,
  breakCount,
  displayMs,
  formatClock,
  gameLengthMs,
  isBreak,
  isFieldId,
  longestPointMs,
  PERIOD_LABEL,
  pointLengthMs,
  tallyPlayers,
  timeoutLeftMs,
  type Player,
  type FieldId,
  type GameState,
  type GameTeam,
  gameSlots,
  resolveSlot,
  slotLabel,
  type Division,
  type Pool,
  type ScheduledGame,
  type Team,
} from "../../shared/types";
import { finalHoldMs, holdsFinal, resolveScene } from "../../shared/overlay-scene";
import type { OverlaySettings } from "../../shared/settings";
import {
  isHoldMode,
  isPreviewMode,
  LiveFeed,
  PreviewFeed,
  useAutoSceneInputs,
  useOtherFields,
  useOverlayFeed,
  useTournamentData,
} from "../lib/overlay-feed";
import { useNow } from "../lib/useGameState";
import { overlayRoute } from "../router";
import "./overlay.css";
// Generated from motion's own spring generator by tools/springs.ts. Separate
// file because it is generated; imported here so it rides this page's chunk.
import "./overlay-motion.css";

/** OBS Browser Source page. Transparent; renders the bug or the break card. */
export function OverlayPage() {
  const { field } = overlayRoute.useParams();

  useEffect(() => {
    document.documentElement.classList.add("ovl-mode");
    return () => document.documentElement.classList.remove("ovl-mode");
  }, []);

  if (!isFieldId(field)) return null;
  // `?preview=1` swaps the socket for frames posted in by an embedder — that
  // is the whole of what the /super showcase needs from this page, and it
  // means the showcase drives the REAL graphic rather than a copy of it.
  // Everything below this line is identical on both paths.
  if (isPreviewMode()) {
    return (
      <PreviewFeed>
        <OverlayScene field={field} />
      </PreviewFeed>
    );
  }
  return (
    <LiveFeed field={field}>
      <OverlayScene field={field} />
    </LiveFeed>
  );
}

function OverlayScene({ field }: { field: FieldId }) {
  const { state, skew } = useOverlayFeed();
  const auto = useAutoSceneInputs(state, skew);
  // Where "auto" becomes a real scene. A pinned scene passes through verbatim;
  // shared/overlay-scene.ts has the rules and the tests.
  const scene = state === null ? null : resolveScene(state, auto.nextStartAtMs, auto.now, auto.overlay);
  /**
   * The final card, remembered past the state that carried it: "Set up game"
   * wipes the finished match from the live state, and a keeper who preps the
   * next slot twenty seconds after the horn used to cut the final score off
   * stream mid-hold. It holds over the whole of the next game's PREGAME -
   * schedule and intro alike - for the configured window; play and a pinned
   * scene outrank it (`holdsFinal` has the reasoning).
   */
  const finalCardRef = useRef<{ state: GameState; until: number } | null>(null);
  if (state !== null && state.period === "final" && state.finalAt !== undefined) {
    // The SAME hold the resolver uses: the organiser's number, giving way when
    // the next game on this field is due (`finalHoldMs`). `auto.nextStartAtMs`
    // is that next game's start while the period is final.
    finalCardRef.current = {
      state,
      until: state.finalAt + finalHoldMs(auto.overlay, state.finalAt, auto.nextStartAtMs),
    };
  }
  const held = finalCardRef.current;
  const heldFinal =
    state !== null && held !== null && holdsFinal(state, held.until, auto.now) ? held.state : null;
  const prevRef = useRef<GameState | null>(null);
  const [glow, setGlow] = useState<{ side: "A" | "B"; at: number } | null>(null);
  const [probAt, setProbAt] = useState<number | null>(null);
  const [lowerThird, setLowerThird] = useState<LowerThird | null>(null);
  const [breakFlash, setBreakFlash] = useState<{ color: string; at: number } | null>(null);
  const [topAt, setTopAt] = useState<number | null>(null);
  /** When the leading-scorer panel is next due. A ref so a scene change cannot
   *  reset it — see the cycle below. */
  const topDueRef = useRef<number | null>(null);

  // Diff consecutive states → score flash + break badge. (The timeout badge is
  // not an edge any more: it reads `state.timeout`, see `TimeoutBadge`.)
  useEffect(() => {
    if (state === null) return;
    const prev = prevRef.current;
    prevRef.current = state;
    if (prev === null || prev.v >= state.v) return;

    for (const side of ["A", "B"] as const) {
      const before = side === "A" ? prev.teamA : prev.teamB;
      const after = side === "A" ? state.teamA : state.teamB;

      if (after.score > before.score && scene === "live") {
        const now = Date.now();
        setGlow({ side, at: now });

        // The point that was just added, if point tracking is on.
        const point = (state.points ?? [])[(state.points ?? []).length - 1];
        const scored = point !== undefined && point.team === side;

        if (scored && isBreak(point)) {
          setBreakFlash({ color: after.color, at: now });
          // Held open by the showcase so the corner's stacking can be looked
          // at; on a Browser Source `isHoldMode` is false by construction.
          if (!isHoldMode()) setTimeout(() => setBreakFlash(null), 2600);
        }

        // Win probability always shows on a score. Credit arrives later (the
        // scorekeeper picks the scorer after the point lands), so the scorer
        // card is triggered separately below and lives on the other side.
        setProbAt(now);
      }
    }
  }, [state]);

  // Credit lands AFTER the point (the scorekeeper picks the scorer once the
  // score is already on the board), so the scorer card is driven by the latest
  // point gaining a scorer — not by the score changing.
  const lastPoint = state?.points?.[state.points.length - 1] ?? null;
  const creditKey =
    lastPoint?.scorerId != null ? `${state?.points?.length ?? 0}:${lastPoint.scorerId}:${lastPoint.assistId ?? ""}` : null;

  useEffect(() => {
    if (creditKey === null || state === null || lastPoint === null) return;
    const team = lastPoint.team === "A" ? state.teamA : state.teamB;
    const scorer = findPlayer(team, lastPoint.scorerId);
    if (scorer === null) return;
    setLowerThird({
      at: Date.now(),
      color: team.color,
      teamShort: team.short,
      scorer,
      assist: findPlayer(team, lastPoint.assistId),
    });
    if (isHoldMode()) return;
    const t = setTimeout(() => setLowerThird(null), LT_TOTAL_MS);
    return () => clearTimeout(t);
    // Keyed on the credit itself so re-renders don't replay the card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditKey]);

  const mode =
    state === null || scene === null
      ? null
      : heldFinal !== null
        ? "final"
        : scene === "live"
          ? liveMode(state)
          : scene;
  // When the current PERIOD began, by this client's clock. The intro's settle
  // is measured from here rather than from the moment the card came due, so a
  // final-card hold that ends twenty seconds into a pregame shows the rosters
  // at once, while a two-second pregame between final and the pull never does.
  const period = state?.period ?? null;
  const [periodSince, setPeriodSince] = useState(0);
  useEffect(() => {
    setPeriodSince(period === null ? 0 : Date.now());
  }, [period]);
  // The intro has to SETTLE before the stinger takes it on: see `useSettled`.
  const settled = useSettled(mode, periodSince);
  const { shown, wipeKey } = useWipe(settled);

  // Leading scorers surface on their own every few minutes so the corner has
  // something to say between points, then clear themselves.
  //
  // Driven by a DUE TIME on a ref, not by an interval started when the scene
  // is: this effect is keyed on `[shown]`, so the old `setInterval` was torn
  // down and rebuilt every time the overlay left and re-entered `live` — and
  // an interval does not fire until a whole period has passed. Halftime, a
  // break and the pregame card each pushed the panel a full eight minutes into
  // the future, which is why on a short or interrupted game it never appeared
  // at all. The due time survives those, and the first one is deliberately
  // early: `TopScorers` renders nothing when nobody has scored yet, so an
  // attempt that finds no story simply costs nothing.
  useEffect(() => {
    if (shown !== "live") return;
    // ?top=now forces the panel immediately — used to preview it without
    // waiting out a cycle.
    if (new URLSearchParams(window.location.search).get("top") === "now") {
      setTopAt(Date.now());
    }
    if (topDueRef.current === null) topDueRef.current = Date.now() + TOP_FIRST_MS;
    const tick = setInterval(() => {
      const due = topDueRef.current;
      if (due === null || Date.now() < due) return;
      topDueRef.current = Date.now() + TOP_EVERY_MS;
      setTopAt(Date.now());
      setTimeout(() => setTopAt(null), TOP_VISIBLE_MS + LT_EXIT_MS);
    }, TOP_TICK_MS);
    return () => clearInterval(tick);
  }, [shown]);

  if (state === null || shown === null) return null;

  return (
    // `ovl-hold` is the showcase's Hold: it swaps the self-clearing animations
    // for ones that rest visible. Unreachable on a Browser Source.
    <div className={isHoldMode() ? "ovl-root ovl-hold" : "ovl-root"}>
      {/* Every scene animates in with CSS keyframes that end fully visible.
          Nothing animates OUT: a scene only ever changes underneath the stinger
          wipe, which covers the frame from 40% to 60% while `useWipe` swaps the
          content at the halfway point, so an exit was invisible by
          construction — and a JS exit that a throttled Browser Source never
          finished is how a takeover once froze at 11% opacity on stream. */}
      {shown === "live" && (
        <div key="live" className="ovl-enter-bug">
          <ScoreBug state={state} skew={skew} glow={glow} breakFlash={breakFlash} />
        </div>
      )}
      {shown === "final" && (
        <div key="final" className="ovl-enter-final">
          <FinalBoard state={heldFinal ?? state} />
        </div>
      )}
      {shown === "intro" && <IntroCard key="intro" state={state} />}
      {shown === "halftime" && <HalftimeCard key="halftime" state={state} />}
      {shown === "break" && (
        <div key="break" className="ovl-enter-break">
          <BreakCard field={field} skew={skew} overlay={auto.overlay} />
        </div>
      )}

      {/* Bottom-LEFT: stats that follow a point. Top-scorer panel outranks win
          probability when it takes its turn. */}
      {shown === "live" && topAt !== null && <TopScorers state={state} showAt={topAt} />}
      {shown === "live" && topAt === null && <WinProbability state={state} showAt={probAt} />}

      {/* Bottom-RIGHT: who scored. Independent slot, so crediting a point never
          competes with the probability bar. */}
      {shown === "live" && lowerThird !== null && <ScorerLowerThird data={lowerThird} />}

      {/* KEYED. Without a key React reuses the node and a CSS animation does
          not restart, so the stinger played once per Browser Source load and
          never again for the rest of the event. */}
      {wipeKey !== null && (
        <div key={wipeKey} className="ovl-wipe">
          <div className="ovl-wipe-panel" />
          <div className="ovl-wipe-panel" />
        </div>
      )}
    </div>
  );
}

/** CSS exit durations, mirrored from overlay.css so the unmount waits them out. */
const BANNER_OUT_MS = 200;
const TOAST_OUT_MS = 200;

const WIPE_MS = 900;
/** Panels fully cover the frame between 40% and 60% — swap content at the middle. */
const WIPE_SWAP_MS = WIPE_MS * 0.5;

type OverlayMode = "live" | "final" | "intro" | "halftime" | "break" | "hidden";

/**
 * Plays a stinger wipe when the overlay changes state, holding the previous
 * content until the panels cover the frame. The first mode never wipes: a
 * Browser Source loading mid-game should simply appear.
 */
function useWipe(mode: OverlayMode | null): { shown: OverlayMode | null; wipeKey: number | null } {
  const [shown, setShown] = useState<OverlayMode | null>(null);
  // A KEY, not a boolean. Two things depend on that: React must remount the
  // element for its CSS animation to restart, and the end of one wipe must not
  // be able to tear down the beginning of the next.
  const [wipeKey, setWipeKey] = useState<number | null>(null);
  const seq = useRef(0);
  const endRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (mode === null || mode === shown) return;
    // A Browser Source loading mid-game should simply appear, not wipe on.
    if (shown === null) {
      setShown(mode);
      return;
    }
    seq.current += 1;
    setWipeKey(seq.current);
    const swap = setTimeout(() => setShown(mode), WIPE_SWAP_MS);
    // Deliberately NOT torn down with this effect. `setShown` above re-runs it
    // at the halfway point, and cleaning up here cancelled the timer that ends
    // the wipe — so `wiping` latched true, the full-frame layer stayed mounted
    // for the rest of the session, and because React then reused that node the
    // stinger never played a second time.
    clearTimeout(endRef.current);
    endRef.current = setTimeout(() => setWipeKey(null), WIPE_MS);
    return () => clearTimeout(swap);
  }, [mode, shown]);

  useEffect(() => () => clearTimeout(endRef.current), []);

  return { shown, wipeKey };
}

/**
 * How long the pregame STATE must have stood before the overlay wipes to the
 * intro card. Asked of the intro only.
 *
 * On a day running late the keeper loads the next game AFTER its scheduled
 * start, so its intro is due the instant setup lands, and the pull follows a
 * breath later: the stream got FINAL, wipe, two seconds of rosters, wipe,
 * score bug. A period change is a moment the sideline just called - the bug,
 * the halftime card and the final board must land at once - but the intro is
 * a WINDOW opening, and six seconds late on a two-minute window nobody sees.
 * A card that would not have stayed up six seconds was never really up.
 *
 * Measured from `since` - when the pregame state arrived - and not from when
 * the card came due: a previous game's final card that holds twenty seconds
 * into the pregame (`holdsFinal`) has already given the keeper's next tap all
 * the time it needed, and the rosters must not wait another six.
 */
const INTRO_SETTLE_MS = 6_000;

function useSettled(mode: OverlayMode | null, since: number): OverlayMode | null {
  const [settled, setSettled] = useState(mode);
  useEffect(() => {
    if (mode === settled) return;
    // A Browser Source loading straight into pregame shows the card at once:
    // there is nothing on screen for it to flicker against.
    if (mode !== "intro" || settled === null) {
      setSettled(mode);
      return;
    }
    const wait = Math.max(0, INTRO_SETTLE_MS - (Date.now() - since));
    if (wait === 0) {
      setSettled(mode);
      return;
    }
    const t = setTimeout(() => setSettled(mode), wait);
    return () => clearTimeout(t);
  }, [mode, settled, since]);
  return settled;
}

/**
 * Which takeover the "live" scene actually shows. Intro is gated on
 * `configured` so an untouched field never sits on a placeholder matchup;
 * states saved before that flag existed fall through to the score bug.
 */
/** The crest for a slot, by that slot's GROUND: dark grounds prefer the
 *  team's explicit dark-ground variant (`logoDark` - see `Team.logoDark`),
 *  and everything falls back to the primary. Undefined only when the team
 *  has no crest at all. */
function logoFor(team: GameTeam, darkGround: boolean): string | undefined {
  return darkGround ? (team.logoDark ?? team.logo) : team.logo;
}

function liveMode(state: GameState): "live" | "final" | "intro" | "halftime" {
  if (state.period === "final") return "final";
  if (state.period === "pregame" && state.configured === true) return "intro";
  if (state.period === "half") return "halftime";
  return "live";
}

// ---------- shared color helpers ----------

function rgbOf(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (m?.[1] === undefined) return null;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join("") : m[1];
  return [
    Number.parseInt(h.slice(0, 2), 16),
    Number.parseInt(h.slice(2, 4), 16),
    Number.parseInt(h.slice(4, 6), 16),
  ];
}

/** Black or white, whichever reads on the team color. */
function onColor(hex: string): string {
  const rgb = rgbOf(hex);
  if (rgb === null) return "#fff";
  const [r, g, b] = rgb;
  return (r * 299 + g * 587 + b * 114) / 1000 >= 150 ? "#0c0d10" : "#ffffff";
}

// ---------- live score bug ----------

function ScoreBug({
  state,
  skew,
  glow,
  breakFlash,
}: {
  state: GameState;
  skew: number;
  glow: { side: "A" | "B"; at: number } | null;
  breakFlash: { color: string; at: number } | null;
}) {
  const { fieldName } = useOverlayFeed();
  const banner = state.banner ?? autoBanner(state);
  const timeouts = state.config.timeoutsPerHalf;

  return (
    <div className="ovl-bug">
      {/* Hairline, score row and the tabs under it are ONE sheared stack, so
          their edges stay collinear. The banner and the toasts lean separately
          because they come and go — see `.ovl-lean` in overlay.css. */}
      <div className="ovl-plate ovl-lean">
        {/* The two team colours ride the hairline; the CSS puts the brand pink
            between them. */}
        <div
          className="ovl-topline"
          style={{ "--ca": state.teamA.color, "--cb": state.teamB.color } as React.CSSProperties}
        />
        {/* `--tabs` squares off the two outer bottom corners, which is only
            right when there are timeout tabs hanging from them. */}
        <div className={timeouts > 0 ? "ovl-row ovl-row--tabs" : "ovl-row"}>
          <TeamSeg team={state.teamA} onOffense={state.offense === "A"} />
          <ScoreSeg team={state.teamA} glowing={glow?.side === "A" ? glow.at : null} />
          <MidSeg state={state} skew={skew} fieldName={fieldName} />
          <ScoreSeg team={state.teamB} glowing={glow?.side === "B" ? glow.at : null} />
          <TeamSeg team={state.teamB} onOffense={state.offense === "B"} />
        </div>

        <div className="ovl-sub">
          <TimeoutTicks team={state.teamA} total={timeouts} />
          <TimeoutTicks team={state.teamB} total={timeouts} />
        </div>
      </div>

      <Banner text={banner} />
      {/* KEYED on the moment of the break: a second break while the first badge
          is still up would otherwise reuse the node, and a CSS animation does
          not restart on a node React keeps. */}
      {breakFlash !== null && <BreakFlash key={breakFlash.at} color={breakFlash.color} />}
      <TimeoutBadge state={state} skew={skew} />
    </div>
  );
}

/**
 * TIMEOUT · KYV · 1:15, counting down under the bug.
 *
 * Driven by `state.timeout` rather than by diffing consecutive states: WFDF
 * gives a timeout seventy-five seconds, the sideline counts them, and a
 * four-second toast that said only WHO had called it left the stream with
 * nothing to say for the other seventy-one. Rendered from `{team, at}` plus
 * the server skew the way the game clock is - no per-second broadcast, and
 * the overlay, the board and the keeper's phone all read the same second. A
 * Browser Source reloaded mid-timeout picks the countdown up where it is.
 *
 * The badge is remembered past the moment its timeout ends (the seconds run
 * out, a score, a give-back) so the exit has something to play on. The exit
 * is the toast's CSS animation, which ENDS invisible; the unmount after it is
 * a timer, and a throttled source that never fires it is left with a node the
 * keyframes already hid - the failure the takeovers' JS exits could not rule
 * out. Hold (showcase only) keeps a badge whose seconds are up.
 */
function TimeoutBadge({ state, skew }: { state: GameState; skew: number }) {
  // The showcase pins the clock so a fixture can sit on "0:55 left"; live, the
  // resolver's own tick is too coarse for seconds, so this has its own.
  const { previewNow } = useOverlayFeed();
  const wall = useNow(skew);
  const now = previewNow ?? wall;
  const live = state.timeout;
  const running =
    live !== undefined && (timeoutLeftMs(live, now) !== null || isHoldMode()) ? live : null;
  const runningKey = running === null ? null : `${running.team}:${running.at}`;

  const [shown, setShown] = useState<NonNullable<GameState["timeout"]> | null>(running);
  const [leaving, setLeaving] = useState(false);
  const shownKey = shown === null ? null : `${shown.team}:${shown.at}`;
  // The latest stamp, for the effect below. The effect is keyed on the
  // PRIMITIVE: every state frame carries a fresh `timeout` object even when it
  // is the same timeout, and keying on the object would restart the exit
  // timer on every message.
  const runningRef = useRef(running);
  runningRef.current = running;

  useEffect(() => {
    if (runningKey === shownKey) return;
    const next = runningRef.current;
    if (next !== null) {
      // A new timeout (or the other team's while this one is still up): show
      // it, cancelling any exit in flight.
      setLeaving(false);
      setShown(next);
      return;
    }
    // Over: arm the exit, unmount once it has played.
    setLeaving(true);
    const t = setTimeout(() => {
      setShown(null);
      setLeaving(false);
    }, TOAST_OUT_MS);
    return () => clearTimeout(t);
  }, [runningKey, shownKey]);

  if (shown === null) return null;
  const team = shown.team === "A" ? state.teamA : state.teamB;
  // Whole seconds, rounded UP: 1:15 the instant the timeout is called, 0:01 in
  // its last second, and never a 0:00 that is somehow still counting.
  const seconds = Math.ceil((timeoutLeftMs(shown, now) ?? 0) / 1000);
  return (
    // KEYED on the stamp, so a second timeout replays the arrival instead of
    // reusing the node - a CSS animation does not restart on a node React keeps.
    <div key={shown.at} className="ovl-lean">
      <div
        className={leaving ? "ovl-toast ovl-toast--out" : "ovl-toast"}
        style={{ "--tclr": team.color } as React.CSSProperties}
      >
        <span className="ovl-toast-text ovl-race">TIMEOUT · {team.short}</span>
        <ClockText className="ovl-toast-clock ovl-race" text={formatClock(seconds * 1000)} />
      </div>
    </div>
  );
}

function TeamSeg({ team, onOffense }: { team: GameTeam; onOffense: boolean }) {
  return (
    <div
      className="ovl-seg ovl-seg--team"
      // `--oink` is black-or-white against this team's colour, computed once
      // here and used by both the team code and the possession bracket.
      style={{ background: team.color, "--oink": onColor(team.color) } as React.CSSProperties}
    >
      {/* Full-colour crest, but the VARIANT is picked by this panel's own
          ground: a dark team colour (white text up in `--oink`) gets the
          dark-ground variant when the team shipped one. A peach wordmark on
          its own peach panel was invisible; recolouring procedurally
          flattened multi-colour crests, so the fix is explicit art
          (`logoDark`), never a filter. */}
      {logoFor(team, onColor(team.color) === "#ffffff") !== undefined && (
        <img
          className="ovl-logo"
          src={logoFor(team, onColor(team.color) === "#ffffff")}
          alt=""
        />
      )}
      <span className="ovl-short ovl-race">{team.short}</span>
      {/* Which side began this point on offence. `state.offense` is exactly
          that between scores: the reducer flips it the instant a point lands
          (whoever scored pulls, the other receives), so while a point is being
          played it names the receiving team. START of point only — turnovers
          are not tracked, and nothing here claims otherwise. */}
      {onOffense && <span className="ovl-offense-rule" />}
    </div>
  );
}

/** Settle time of the digit springs, from `overlay-motion.css`. */
const DIGIT_MS = 463;

function ScoreSeg({ team, glowing }: { team: GameTeam; glowing: number | null }) {
  const roll = useScoreRoll(team.score);
  return (
    <div className="ovl-seg ovl-seg--score" style={{ "--glow": team.color } as React.CSSProperties}>
      {/* Keyed on the moment of the score, so a new point replays the flash.
          This used to be a class on the segment plus a key on the segment
          itself — which remounted the digits underneath and threw away the
          roll on every single point, so it never actually played. */}
      {glowing !== null && <span key={glowing} className="ovl-glow-flash" />}
      <span className="ovl-score-num ovl-race">
        {roll !== null && (
          <span key={`out-${roll.key}`} className="ovl-score-digit ovl-score-digit--out">
            {roll.from}
          </span>
        )}
        <span
          key={team.score}
          className={roll !== null ? "ovl-score-digit ovl-score-digit--in" : "ovl-score-digit"}
        >
          {team.score}
        </span>
      </span>
    </div>
  );
}

/**
 * The digit leaving, while a new one arrives.
 *
 * The state is adjusted DURING render rather than in an effect: an effect runs
 * after paint, so the new digit would show for one frame at its resting
 * position before jumping up to start its animation. React re-renders
 * immediately on a set-during-render and never commits the intermediate value.
 */
function useScoreRoll(score: number): { from: number; key: number } | null {
  const [seen, setSeen] = useState(score);
  const [roll, setRoll] = useState<{ from: number; key: number } | null>(null);

  if (seen !== score) {
    setSeen(score);
    // First mount never rolls; only a CHANGE does.
    setRoll({ from: seen, key: (roll?.key ?? 0) + 1 });
  }

  const key = roll?.key ?? null;
  useEffect(() => {
    if (key === null) return;
    // Cleared once the animation has finished, so the class can come off
    // without cancelling anything mid-flight.
    const t = setTimeout(() => setRoll(null), DIGIT_MS);
    return () => clearTimeout(t);
  }, [key]);

  return roll;
}

/**
 * GAME POINT / cap banner. Mounted through its own exit so the departure is a
 * CSS animation like the arrival: `AnimatePresence` used to hold it there, and
 * a JS exit is the thing that can strand a graphic on a throttled source.
 */
function Banner({ text }: { text: string | null }) {
  const [shown, setShown] = useState(text);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (text === shown) return;
    if (text !== null) {
      setLeaving(false);
      setShown(text);
      return;
    }
    setLeaving(true);
    const t = setTimeout(() => {
      setShown(null);
      setLeaving(false);
    }, BANNER_OUT_MS);
    return () => clearTimeout(t);
  }, [text, shown]);

  if (shown === null) return null;
  return (
    // The lean is a WRAPPER, not a class on the banner: `.ovl-banner` animates
    // its own transform, and an animated transform replaces the static one
    // rather than composing with it.
    <div className="ovl-lean">
      <div className={leaving ? "ovl-banner ovl-race ovl-banner--out" : "ovl-banner ovl-race"}>
        {/* keyed span: a text swap replays a cheap one-shot CSS pop, without
            remounting (and so re-animating) the block around it */}
        <span key={shown} className="ovl-banner-text">
          {shown}
        </span>
      </div>
    </div>
  );
}



function MidSeg({
  state,
  skew,
  fieldName,
}: {
  state: GameState;
  skew: number;
  fieldName: string;
}) {
  const now = useNow(skew);
  const ms = displayMs(state, now);
  const low = state.config.clockMode === "countdown" && state.clock.running && ms <= 2 * 60_000;

  const clockClass = ["ovl-clock", "ovl-race"];
  if (low) clockClass.push("ovl-clock--low");
  else if (!state.clock.running) clockClass.push("ovl-clock--stopped");

  return (
    <div className="ovl-seg ovl-seg--mid">
      <ClockText className={clockClass.join(" ")} text={formatClock(ms)} />
      <span className="ovl-period ovl-race">{PERIOD_LABEL[state.period]}</span>
      {/* The field name hangs off THIS block rather than sitting in the strip
          below the row. In that strip it was centred on the row, and the row is
          only symmetric until a score reaches two digits or one team turns out
          to have a logo the other does not — either one walked the field name
          out from under the clock. */}
      <div className="ovl-field-chip">
        <span className="ovl-field-text ovl-race">{fieldName.toUpperCase()}</span>
      </div>
    </div>
  );
}

/** Fixed-width per glyph — NXT Font has no tabular digits. */
function ClockText({ text, className }: { text: string; className: string }) {
  return (
    <span className={className}>
      {[...text].map((ch, i) => (
        <span key={i} className={ch === ":" ? "ovl-colon" : undefined}>
          {ch}
        </span>
      ))}
    </span>
  );
}

/**
 * The timeout pips, on a tab in the team's colour hanging off that team's block.
 * Takes the whole team rather than a count because the tab needs the colour and
 * the pips need `onColor` of it — the same call the team code above them makes.
 */
function TimeoutTicks({ team, total }: { team: GameTeam; total: number }) {
  // `timeoutsPerHalf: 0`: hold the slot so the field chip stays centred, but
  // paint no tab. An empty coloured plate hanging off the block reads as a
  // graphic that failed to finish loading.
  if (total === 0) return <div className="ovl-ticks ovl-ticks--none" />;
  return (
    <div
      className="ovl-ticks"
      style={{ "--tclr": team.color, "--tink": onColor(team.color) } as React.CSSProperties}
    >
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className={i < team.timeoutsLeft ? "ovl-tick" : "ovl-tick ovl-tick--used"} />
      ))}
    </div>
  );
}

// ---------- final board (bottom center, shown when period = final) ----------

function FinalBoard({ state }: { state: GameState }) {
  const { fieldName } = useOverlayFeed();
  return (
    <div className="ovl-final">
      {/* Badge and bar shear together, pivoting on the middle because this
          graphic is centred in the frame rather than anchored to an edge. The
          meta chips below stay outside: they lean already. */}
      <div className="ovl-final-plate ovl-lean ovl-lean--center">
        <div className="ovl-final-chip">
          <span className="ovl-final-chip-text ovl-race">FINAL</span>
        </div>
        <div className="ovl-final-row">
          <div className="ovl-final-strip" style={{ background: state.teamA.color }} />
          <FinalTeam team={state.teamA} side="A" />
          <div className="ovl-final-score">
            <span className="ovl-race">{state.teamA.score}</span>
            <span className="ovl-race ovl-final-dash">–</span>
            <span className="ovl-race">{state.teamB.score}</span>
          </div>
          <FinalTeam team={state.teamB} side="B" />
          <div className="ovl-final-strip" style={{ background: state.teamB.color }} />
        </div>
      </div>
      <div className="ovl-take-meta">
        <Chip>{fieldName.toUpperCase()}</Chip>
        <BreaksChip state={state} />
      </div>
    </div>
  );
}

function FinalTeam({ team, side }: { team: GameTeam; side: "A" | "B" }) {
  return (
    <div className={side === "A" ? "ovl-final-team" : "ovl-final-team ovl-final-team--b"}>
      {/* The band is the one fixed dark ground, where a black crest simply
          vanished - so this slot always prefers the dark-ground variant. */}
      {logoFor(team, true) !== undefined && (
        <img className="ovl-final-logo" src={logoFor(team, true)} alt="" />
      )}
      <span className="ovl-final-name ovl-race">{team.name}</span>
    </div>
  );
}

// ---------- scorer lower third / break flash / top scorer ----------

const LT_VISIBLE_MS = 5200;
const LT_EXIT_MS = 300;
const LT_TOTAL_MS = LT_VISIBLE_MS + LT_EXIT_MS;

interface LowerThird {
  at: number;
  color: string;
  teamShort: string;
  scorer: Player;
  assist: Player | null;
}

function findPlayer(team: GameTeam, id: string | null): Player | null {
  if (id === null) return null;
  return (team.roster ?? []).find((p) => p.id === id) ?? null;
}

/** "SCORE · #7 Petrenko / assist #12 Kovalenko" band after a credited point. */
function ScorerLowerThird({ data }: { data: LowerThird }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    setLeaving(false);
    const t = setTimeout(() => setLeaving(true), LT_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [data.at]);

  return (
    <div className={leaving ? "ovl-lt ovl-lt--out" : "ovl-lt"}>
      <span className="ovl-lt-flag" style={{ background: data.color }} />
      <span
        className="ovl-lt-kind ovl-race"
        style={{ background: data.color, color: onColor(data.color) }}
      >
        SCORE
      </span>
      <span className="ovl-lt-body">
        <span className="ovl-lt-main ovl-race">
          {data.scorer.number !== "" && <span className="ovl-lt-num ovl-race">#{data.scorer.number}</span>}
          {data.scorer.name}
        </span>
        <span className="ovl-lt-sub ovl-race">
          {data.teamShort}
          {data.assist !== null
            ? ` · ASSIST ${data.assist.number !== "" ? `#${data.assist.number} ` : ""}${data.assist.name.toUpperCase()}`
            : ""}
        </span>
      </span>
    </div>
  );
}

/**
 * A meta chip, leaning with the type. Used by every takeover and by the final
 * board, so the row of them reads as one set wherever it appears.
 *
 * The lean is a skew on the plate with the text sheared back upright, which is
 * why the text needs an element of its own — and why this is a component rather
 * than a class each call site has to remember to nest correctly.
 */
function Chip({ accent = false, children }: { accent?: boolean; children: ReactNode }) {
  return (
    <span className={accent ? "ovl-take-chip ovl-take-chip--accent" : "ovl-take-chip"}>
      <span className="ovl-take-chip-text ovl-race">{children}</span>
    </span>
  );
}

/** "BREAKS 2–1", omitted when no breaks have happened (or tracking is off). */
function BreaksChip({ state }: { state: GameState }) {
  const points = state.points ?? [];
  const a = breakCount(points, "A");
  const b = breakCount(points, "B");
  if (a + b === 0) return null;
  return (
    <Chip>
      BREAKS {a}–{b}
    </Chip>
  );
}

/** Lives in the score bug's column, so it can never land on the toast. */
function BreakFlash({ color }: { color: string }) {
  return (
    <div className="ovl-lean">
      <div className="ovl-break-flash" style={{ background: color, color: onColor(color) }}>
        <span className="ovl-break-flash-text ovl-race">BREAK</span>
      </div>
    </div>
  );
}

const TOP_VISIBLE_MS = 7000;
/** How often the leading-scorer panel takes its turn in the corner. Games run
 *  ~60 min and points are infrequent, so this stays rare on purpose. */
const TOP_EVERY_MS = 8 * 60_000;
/** The FIRST turn comes early. Eight minutes into a half is most of the way to
 *  a running clock's cap, and the panel suppresses itself when there is nothing
 *  to say, so an early attempt is free. */
const TOP_FIRST_MS = 3 * 60_000;
/** How often the due time is checked. Coarse on purpose: this decides when a
 *  panel appears, not what a clock reads. */
const TOP_TICK_MS = 15_000;

/** Occasional leading-scorer panel; same auto-hide rhythm as win probability. */
function TopScorers({ state, showAt }: { state: GameState; showAt: number | null }) {
  const [phase, setPhase] = useState<"hidden" | "in" | "out">("hidden");

  useEffect(() => {
    if (showAt === null) return;
    const age = Date.now() - showAt;
    if (age >= TOP_VISIBLE_MS) return;
    setPhase("in");
    const leave = setTimeout(() => setPhase("out"), TOP_VISIBLE_MS - age);
    const gone = setTimeout(() => setPhase("hidden"), TOP_VISIBLE_MS - age + LT_EXIT_MS);
    return () => {
      clearTimeout(leave);
      clearTimeout(gone);
    };
  }, [showAt]);

  const points = state.points ?? [];
  const rows: { team: GameTeam; player: Player; goals: number; assists: number }[] = [];
  for (const side of ["A", "B"] as const) {
    const team = side === "A" ? state.teamA : state.teamB;
    const best = tallyPlayers(points, side)[0];
    if (best === undefined || best.goals === 0) continue;
    const player = findPlayer(team, best.playerId);
    if (player === null) continue;
    rows.push({ team, player, goals: best.goals, assists: best.assists });
  }

  // Something the score alone cannot say, in the slot that is already open.
  const longest = longestPointMs(points);

  if (phase === "hidden" || rows.length === 0) return null;

  return (
    <div className={phase === "out" ? "ovl-top ovl-top--out" : "ovl-top"}>
      <div className="ovl-top-head ovl-race">LEADING SCORERS</div>
      {rows.map((r) => (
        <div key={r.team.short} className="ovl-top-row">
          <span className="ovl-race" style={{ color: r.team.color }}>
            {r.team.short}
          </span>
          <span>
            {r.player.number !== "" ? `#${r.player.number} ` : ""}
            {r.player.name}
          </span>
          <span className="ovl-top-stat ovl-race">
            {r.goals}G{r.assists > 0 ? ` ${r.assists}A` : ""}
          </span>
        </div>
      ))}
      {longest !== null && (
        <div className="ovl-top-foot ovl-race">LONGEST POINT {formatClock(longest)}</div>
      )}
    </div>
  );
}

// ---------- win probability ----------

const PROB_VISIBLE_MS = 8000;
const PROB_EXIT_MS = 320;

/**
 * Bottom-left probability bar. Appears for a few seconds after each score,
 * then leaves — persistent would clutter the frame and stop being interesting.
 */
function WinProbability({ state, showAt }: { state: GameState; showAt: number | null }) {
  const [phase, setPhase] = useState<"hidden" | "in" | "out">("hidden");

  useEffect(() => {
    if (showAt === null) return;
    // Remounting (e.g. returning from the halftime takeover) must not replay a
    // stale trigger — only a score from the last few seconds opens the panel.
    const age = Date.now() - showAt;
    if (age >= PROB_VISIBLE_MS) return;

    setPhase("in");
    const leave = setTimeout(() => setPhase("out"), PROB_VISIBLE_MS - age);
    const gone = setTimeout(() => setPhase("hidden"), PROB_VISIBLE_MS - age + PROB_EXIT_MS);
    return () => {
      clearTimeout(leave);
      clearTimeout(gone);
    };
  }, [showAt]);

  const p = winProbabilityA(state);
  if (phase === "hidden" || p === null) return null;

  // The head is a `space-between` row that only ever had one child in it. The
  // panel already opens on every score, so the point that just finished costs
  // no new real estate and needs no cycle of its own.
  const points = state.points ?? [];
  const lastPoint = pointLengthMs(points, points.length - 1);

  const decided = state.period === "final";
  const pctA = displayPercent(p, decided);
  const pctB = 100 - pctA;
  // Nobody trails at 50/50, and `displayPercent` can round both to 50.
  const trailing = pctA === pctB ? null : pctA > pctB ? "B" : "A";

  return (
    <div className={phase === "out" ? "ovl-wp ovl-wp--out" : "ovl-wp"}>
      <div className="ovl-wp-head ovl-race">
        <span>WIN PROBABILITY</span>
        {lastPoint !== null && <span className="ovl-wp-point">POINT {formatClock(lastPoint)}</span>}
      </div>
      <div className="ovl-wp-row ovl-race">
        <span className={trailing === "A" ? "ovl-wp-side ovl-wp-side--trail" : "ovl-wp-side"}>
          <span className="ovl-wp-dot" style={{ background: state.teamA.color }} />
          {state.teamA.short} {pctA}%
        </span>
        <span className={trailing === "B" ? "ovl-wp-side ovl-wp-side--trail" : "ovl-wp-side"}>
          {pctB}% {state.teamB.short}
          <span className="ovl-wp-dot" style={{ background: state.teamB.color }} />
        </span>
      </div>
      {/* Each fill is full-width and SCALED to its share, anchored to the edge
          it grows from. Animating `width` here meant 600ms of layout on every
          frame; a transform is composited and looks the same. The bar itself is
          sheared, which is what turns the seam between the two into a diagonal
          without either fill having to know where the split is. */}
      <div className="ovl-wp-bar">
        <span
          className="ovl-wp-fill ovl-wp-fill--a"
          style={{ "--share": pctA / 100, background: state.teamA.color } as React.CSSProperties}
        />
        <span
          className="ovl-wp-fill ovl-wp-fill--b"
          style={{ "--share": pctB / 100, background: state.teamB.color } as React.CSSProperties}
        />
        {/* Last, so it reads over both fills. */}
        <span className="ovl-wp-mid" />
      </div>
    </div>
  );
}

// ---------- pregame intro / halftime takeovers ----------

interface TeamRecord {
  label: string | null;
  /** Prior meetings between these two teams, most recent first. */
  h2h: string | null;
}

/**
 * Records + head-to-head for the two teams currently on the field, derived
 * from finished games. Only teams picked from the roster have an id, so ad-hoc
 * custom teams simply get no record line.
 */
function useMatchupInfo(state: GameState): { a: TeamRecord; b: TeamRecord } {
  // Live: fetched on mount and refreshed while the card is up. Preview: handed
  // in by the fixture, no network at all.
  //
  // The refresh replaces an effect keyed on `[teamA.teamId, teamB.teamId]`.
  // Freshness is what that dependency was really buying: a team created after
  // the first fetch is missing from `teams`, `recordOf` then finds no
  // `divisionId`, and the record line silently falls back to the FLAT standings
  // — which is how an open team gets credited with wins over women's teams.
  const { teams, schedule } = useTournamentData(60_000);

  // Records are per-division: the flat table used to credit an open team with
  // wins over women's teams on the pregame card.
  const recordOf = (teamId: string | null): string | null => {
    if (teamId === null) return null;
    const division = teams.find((t) => t.id === teamId)?.divisionId;
    const rows =
      division === undefined
        ? computeStandings(teams, schedule)
        : divisionStandings(teams, schedule, division);
    const row = rows.find((r) => r.team.id === teamId);
    if (row === undefined || row.played === 0) return null;
    return row.draws > 0 ? `${row.wins}-${row.draws}-${row.losses}` : `${row.wins}-${row.losses}`;
  };

  const idA = state.teamA.teamId;
  const idB = state.teamB.teamId;
  let h2h: string | null = null;
  if (idA !== null && idB !== null) {
    const ctx = slotContext(teams, schedule);
    const sideIds = (g: (typeof schedule)[number]): { a: string | null; b: string | null } => {
      const slots = gameSlots(g);
      return {
        a: resolveSlot(slots.a, ctx)?.id ?? null,
        b: resolveSlot(slots.b, ctx)?.id ?? null,
      };
    };
    const meetings = schedule.filter((g) => {
      if (g.result === undefined) return false;
      const { a, b } = sideIds(g);
      return (a === idA && b === idB) || (a === idB && b === idA);
    });
    const last = meetings[meetings.length - 1];
    if (last?.result !== undefined) {
      // Express the last result from team A's point of view.
      const sameOrder = sideIds(last).a === idA;
      const forA = sameOrder ? last.result.scoreA : last.result.scoreB;
      const forB = sameOrder ? last.result.scoreB : last.result.scoreA;
      h2h = `LAST MEETING ${forA}–${forB}`;
    }
  }

  return {
    a: { label: recordOf(idA), h2h },
    b: { label: recordOf(idB), h2h },
  };
}

function IntroCard({ state }: { state: GameState }) {
  const { fieldName } = useOverlayFeed();
  const info = useMatchupInfo(state);
  const target = state.config.scoreTarget;
  const hasRosters = (state.teamA.roster ?? []).length > 0 || (state.teamB.roster ?? []).length > 0;

  return (
    <div className="ovl-take">
      {/* Each team's colour bleeding in from its own edge, the two meeting on
          the stinger's diagonal. Sits above the wash and below the content. */}
      <div className="ovl-vs-ground">
        <div
          className="ovl-vs-ground-half ovl-vs-ground-half--a"
          style={{ color: state.teamA.color }}
        />
        <div
          className="ovl-vs-ground-half ovl-vs-ground-half--b"
          style={{ color: state.teamB.color }}
        />
      </div>

      <div className="ovl-take-inner">
        <span className="ovl-take-kicker ovl-race ovl-in">UP NEXT</span>

        <div className="ovl-vs">
          <VsTeam team={state.teamA} record={info.a.label} side="l" />
          <div className="ovl-vs-mid ovl-in-pop ovl-d2">
            <span className="ovl-vs-word ovl-race">VS</span>
            {/* A chip, not the loose caption halftime uses for this slot: on
                this card it is the only prior between these two and it was
                disappearing into the gap under the VS. */}
            {info.a.h2h !== null && <Chip>{info.a.h2h}</Chip>}
          </div>
          <VsTeam team={state.teamB} record={info.b.label} side="r" />
        </div>

        {hasRosters && (
          <div className="ovl-rosters ovl-in ovl-d2">
            <RosterColumn team={state.teamA} side="l" />
            <span />
            <RosterColumn team={state.teamB} side="r" />
          </div>
        )}

        <Stakes state={state} />

        <div className="ovl-take-meta ovl-in ovl-d4">
          <Chip>{fieldName.toUpperCase()}</Chip>
          {target !== null && <Chip>GAME TO {target}</Chip>}
          {/* The whole game: one clock, paused at the break, so "80 MIN" and
              never "40 MIN HALVES" - a half is however long it turns out. */}
          <Chip>{Math.round(gameLengthMs(state.config) / 60_000)} MIN</Chip>
        </div>
      </div>
    </div>
  );
}

/** 1st, 2nd, 3rd, 4th — pool tables are small, so this never has to be clever. */
function ordinal(n: number): string {
  if (n === 1) return "1ST";
  if (n === 2) return "2ND";
  if (n === 3) return "3RD";
  return `${n}TH`;
}

/**
 * What this game is worth: where each side sits in the table they share, and —
 * only when it is provable — that the winner takes the group.
 *
 * `groupStake` refuses to guess: it claims the decider line only when every
 * other game in the group is already played, so no scoreline has to be invented
 * and no tiebreaker has to be second-guessed. A pregame card that overstates
 * what is on the line is worse than one that stays quiet, and this one stays
 * quiet for scratch games, cross-division games and anything mid-pool.
 */
function Stakes({ state }: { state: GameState }) {
  const { teams, schedule, pools, divisions } = useTournamentData(60_000);
  const stake = groupStake(
    teams,
    schedule,
    state.teamA.teamId,
    state.teamB.teamId,
    state.scheduleGameId ?? null,
  );
  if (stake === null) return null;

  const group =
    stake.poolId !== undefined
      ? (pools.find((p) => p.id === stake.poolId)?.name ?? "POOL")
      : (divisions.find((d) => d.id === stake.divisionId)?.name ?? "DIVISION");
  const label = group.toUpperCase();

  return (
    <div className="ovl-stakes ovl-in ovl-d3">
      <span className="ovl-stakes-line ovl-race">
        {label} · {state.teamA.short} {ordinal(stake.rankA)} · {state.teamB.short}{" "}
        {ordinal(stake.rankB)}
      </span>
      {stake.decidesTop && <Chip accent>WINNER TOPS {label}</Chip>}
    </div>
  );
}

function VsTeam({ team, record, side }: { team: GameTeam; record: string | null; side: "l" | "r" }) {
  return (
    <div className={`ovl-vs-team ${side === "l" ? "ovl-in-l" : "ovl-in-r"} ovl-d1`}>
      {/* The intro's ground is dark under its colour washes: dark variant. */}
      {logoFor(team, true) !== undefined ? (
        <img className="ovl-vs-logo" src={logoFor(team, true)} alt="" />
      ) : (
        <span
          className="ovl-vs-disc ovl-race"
          style={{ background: team.color, color: onColor(team.color) }}
        >
          {team.short}
        </span>
      )}
      <span className="ovl-vs-name ovl-race">{team.name}</span>
      {/* The team's colour under its own name. With two crests and white type
          there was otherwise nothing on this card wearing either team's
          colour. */}
      <span className="ovl-vs-rule" style={{ background: team.color }} />
      {record !== null && <Chip>{record}</Chip>}
    </div>
  );
}

/** Roster is capped so a very large squad can't push the card off screen. */
const ROSTER_SHOWN = 18;

function RosterColumn({ team, side }: { team: GameTeam; side: "l" | "r" }) {
  const roster = [...(team.roster ?? [])].sort(byJersey);
  if (roster.length === 0) return <span />;

  const shown = roster.slice(0, ROSTER_SHOWN);
  const extra = roster.length - shown.length;

  return (
    <div className={side === "l" ? "ovl-roster" : "ovl-roster ovl-roster--r"}>
      <span className="ovl-roster-head ovl-race">
        <span className="ovl-roster-flag" style={{ background: team.color }} />
        ROSTER
      </span>
      {/* Rows drive the column split so both columns fill evenly, top-down. */}
      <div
        className="ovl-roster-grid"
        style={{ "--rows": Math.ceil(shown.length / 2) } as React.CSSProperties}
      >
        {shown.map((p) => (
          <span
            key={p.id}
            className={p.isCaptain ? "ovl-roster-player ovl-roster-player--cap" : "ovl-roster-player"}
          >
            <span className="ovl-roster-num ovl-race">{p.number}</span>
            <span className="ovl-roster-name">
              {p.name}
              {p.isCaptain ? " (C)" : ""}
            </span>
          </span>
        ))}
      </div>
      {extra > 0 && <span className="ovl-roster-more ovl-race">+{extra} MORE</span>}
    </div>
  );
}

/** Numbered players first in jersey order, unnumbered players after by name. */
function byJersey(a: Player, b: Player): number {
  const na = Number.parseInt(a.number, 10);
  const nb = Number.parseInt(b.number, 10);
  const aHas = Number.isFinite(na);
  const bHas = Number.isFinite(nb);
  if (aHas && bHas) return na - nb;
  if (aHas) return -1;
  if (bHas) return 1;
  return a.name.localeCompare(b.name);
}

function HalftimeCard({ state }: { state: GameState }) {
  const { fieldName } = useOverlayFeed();
  const lead =
    state.teamA.score === state.teamB.score
      ? "ALL SQUARE"
      : `${(state.teamA.score > state.teamB.score ? state.teamA : state.teamB).short} LEAD`;

  return (
    <div className="ovl-take">
      <div className="ovl-take-inner">
        <span className="ovl-take-kicker ovl-race ovl-in">HALFTIME</span>

        <div className="ovl-half-score ovl-race ovl-in-pop ovl-d1">
          <span className="ovl-half-side">
            <span className="ovl-half-strip" style={{ background: state.teamA.color }} />
            {state.teamA.score}
          </span>
          <span className="ovl-half-sep">:</span>
          <span className="ovl-half-side">
            {state.teamB.score}
            <span className="ovl-half-strip" style={{ background: state.teamB.color }} />
          </span>
        </div>

        <div className="ovl-vs ovl-vs--names ovl-in ovl-d2">
          <span className="ovl-vs-name ovl-race">{state.teamA.name}</span>
          <span className="ovl-vs-h2h ovl-race">{lead}</span>
          <span className="ovl-vs-name ovl-race">{state.teamB.name}</span>
        </div>

        <HalftimeLeaders state={state} />

        <div className="ovl-take-meta ovl-in ovl-d4">
          <Chip>{fieldName.toUpperCase()}</Chip>
          <BreaksChip state={state} />
          <Chip accent>SECOND HALF NEXT</Chip>
        </div>
      </div>
    </div>
  );
}

/**
 * Who has actually been scoring, one player a side.
 *
 * Halftime is the only takeover with room to say it, and a card carrying two
 * numbers and nothing else gives a viewer nothing to talk about for the length
 * of a break. Renders NOTHING when point tracking or rosters are off — which is
 * most casually-run fields — so it can never leave a hole in the layout.
 */
function HalftimeLeaders({ state }: { state: GameState }) {
  const points = state.points ?? [];
  const rows: { team: GameTeam; player: Player; goals: number; assists: number }[] = [];
  for (const side of ["A", "B"] as const) {
    const team = side === "A" ? state.teamA : state.teamB;
    const best = tallyPlayers(points, side)[0];
    if (best === undefined || best.goals === 0) continue;
    const player = findPlayer(team, best.playerId);
    if (player === null) continue;
    rows.push({ team, player, goals: best.goals, assists: best.assists });
  }
  if (rows.length === 0) return null;

  return (
    <div className="ovl-half-leaders ovl-in ovl-d3">
      <span className="ovl-half-leaders-head ovl-race">LEADING SCORERS</span>
      <div className="ovl-half-leader-row">
        {rows.map((r) => (
          <span key={r.team.short} className="ovl-half-leader">
            <span className="ovl-half-leader-flag" style={{ background: r.team.color }} />
            <span>
              {r.player.number !== "" ? `#${r.player.number} ` : ""}
              {r.player.name}
            </span>
            <span className="ovl-half-leader-stat ovl-race">
              {r.goals}G{r.assists > 0 ? ` ${r.assists}A` : ""}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

interface BreakSide {
  name: string;
  /** The team's own colour once the slot resolves; null while the side is still
   *  a placeholder like "Winner Semifinal 1" and belongs to nobody. */
  color: string | null;
}

/**
 * One side of a break-schedule row, falling back to the slot placeholder so a
 * not-yet-decided bracket game reads "Winner Semifinal 1" instead of "?".
 */
function breakSide(
  game: ScheduledGame,
  side: "a" | "b",
  teams: Team[],
  divisions: Division[],
  schedule: ScheduledGame[],
  pools: Pool[],
): BreakSide {
  const slot = gameSlots(game)[side];
  const resolved = resolveSlot(slot, slotContext(teams, schedule));
  if (resolved !== null) return { name: resolved.name, color: resolved.color };
  return { name: slotLabel(slot, { teams, divisions, schedule, pools }), color: null };
}

/**
 * Which side won. A draw is a real answer here and not a missing one — capped
 * games end level often enough that "nobody is dimmed" has to be a state.
 */
function winnerOf(game: ScheduledGame): "a" | "b" | null {
  const r = game.result;
  if (r === undefined || r.scoreA === r.scoreB) return null;
  return r.scoreA > r.scoreB ? "a" : "b";
}

function BreakTeam({ side, lost }: { side: BreakSide; lost: boolean }) {
  return (
    <span className={lost ? "ovl-break-team ovl-break-team--lost" : "ovl-break-team"}>
      <span
        className={side.color === null ? "ovl-break-flag ovl-break-flag--tbd" : "ovl-break-flag"}
        {...(side.color !== null ? { style: { background: side.color } } : {})}
      />
      <span className="ovl-break-name">{side.name}</span>
    </span>
  );
}

// ---------- break scene (schedule between games) ----------

function BreakCard({
  field,
  skew,
  overlay,
}: {
  field: FieldId;
  skew: number;
  /** The auto scene's timers: the ELSEWHERE strip keeps a finished game for
   *  as long as that field's own final card would hold it. */
  overlay: OverlaySettings;
}) {
  const { fieldName } = useOverlayFeed();
  // The schedule can change under a break scene that stays up for a whole
  // round, so this one keeps the minute refresh the intro card does not need.
  const { teams, schedule, divisions, pools } = useTournamentData(60_000);
  const now = useNow(skew, 1000);

  const mine = schedule.filter((g) => g.fieldId === field);
  // ONE day at a time. A weekend tournament put Sunday's bracket under
  // Saturday's results in a single column, and the card scrolled nowhere.
  // Today's games while there is a today (results included — the evening
  // break scene is the recap); before the event the first day, after it the
  // last, so the card is never blank while the schedule has anything to say.
  // `now` is already server-skewed; `startAt` is a zone-less local string,
  // and the OBS machine stands at the venue, so local date is the right key.
  const dayOf = (g: ScheduledGame): string => g.startAt.slice(0, 10);
  const days = [...new Set(mine.map(dayOf))].sort();
  const d = new Date(now);
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
  const day = days.includes(today)
    ? today
    : (days.find((x) => x > today) ?? days[days.length - 1]);
  const games = mine.filter((g) => dayOf(g) === day);
  const nextId = games.find((g) => g.result === undefined)?.id;
  // Which day only earns ink when there is more than one to tell apart.
  // English permanently: broadcast graphics.
  const dayLabel =
    days.length > 1 && day !== undefined
      ? new Date(`${day}T12:00`).toLocaleDateString("en-GB", { weekday: "long" }).toUpperCase()
      : null;

  return (
    <div className="ovl-break">
      <div className="ovl-break-card">
        <div className="ovl-break-head">
          <span className="ovl-break-title ovl-race">{fieldName.toUpperCase()}</span>
          <span className="ovl-break-sub ovl-race">
            {dayLabel !== null ? `SCHEDULE · ${dayLabel}` : "SCHEDULE"}
          </span>
        </div>

        <div className="ovl-break-list">
          {games.map((g, i) => {
            const won = winnerOf(g);
            return (
              <div
                key={g.id}
                className={rowClass(g, nextId)}
                // The per-row delay the JS spring used to carry. CSS reads it.
                style={{ "--i": i } as React.CSSProperties}
              >
                <span className="ovl-break-time ovl-race">{timeOf(g.startAt)}</span>
                <span className="ovl-break-match">
                  <BreakTeam side={breakSide(g, "a", teams, divisions, schedule, pools)} lost={won === "b"} />
                  <span className="ovl-break-vs ovl-race">VS</span>
                  <BreakTeam side={breakSide(g, "b", teams, divisions, schedule, pools)} lost={won === "a"} />
                  {g.note !== undefined && <span className="ovl-break-note">{g.note}</span>}
                </span>
                {g.result !== undefined && (
                  <span className="ovl-break-result ovl-race">
                    <span className={won === "b" ? "ovl-break-num--lost" : undefined}>
                      {g.result.scoreA}
                    </span>
                    <span>–</span>
                    <span className={won === "a" ? "ovl-break-num--lost" : undefined}>
                      {g.result.scoreB}
                    </span>
                  </span>
                )}
                {g.id === nextId && <NextChip startAt={g.startAt} now={now} />}
              </div>
            );
          })}
          {games.length === 0 && <div className="ovl-break-empty">Schedule coming soon</div>}
        </div>

        <FieldTicker current={field} overlay={overlay} skew={skew} />

        <span className="ovl-break-foot ovl-race">SCORE.KARRTOPELKA.COM</span>
      </div>
    </div>
  );
}

/**
 * What the rest of the tournament is doing, on the one screen with room to say
 * it. The break scene is the moment a viewer has nothing to watch here, which
 * is exactly when they want to know what is happening on the other pitches.
 *
 * Renders nothing when no other field is mid-game — an empty strip labelled
 * ELSEWHERE is worse than no strip. "Mid-game" is decided from the other
 * field's period and final stamp, never from its stored scene pin
 * (`showsOnTicker`): auto is the stored value for every normal game.
 */
function FieldTicker({
  current,
  overlay,
  skew,
}: {
  current: FieldId;
  overlay: OverlaySettings;
  skew: number;
}) {
  const others = useOtherFields(current, overlay, skew);
  if (others.length === 0) return null;

  return (
    <div className="ovl-ticker">
      <span className="ovl-ticker-head ovl-race">ELSEWHERE</span>
      <div className="ovl-ticker-rows">
        {others.map((g) => (
          <span key={g.fieldId} className="ovl-ticker-game">
            <span className="ovl-ticker-field ovl-race">{g.fieldName.toUpperCase()}</span>
            <span className="ovl-ticker-side">
              <span className="ovl-ticker-flag" style={{ background: g.a.color }} />
              <span className="ovl-race">{g.a.short}</span>
            </span>
            <span className="ovl-ticker-score ovl-race">
              {g.a.score}–{g.b.score}
            </span>
            <span className="ovl-ticker-side">
              <span className="ovl-race">{g.b.short}</span>
              <span className="ovl-ticker-flag" style={{ background: g.b.color }} />
            </span>
            {g.final && <span className="ovl-ticker-final ovl-race">FINAL</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

function rowClass(g: ScheduledGame, nextId: string | undefined): string {
  const cls = ["ovl-break-row"];
  if (g.id === nextId) cls.push("ovl-break-row--next");
  if (g.result !== undefined) cls.push("ovl-break-row--done");
  return cls.join(" ");
}

/**
 * "NEXT", becoming "NEXT · IN 12:34" once the game is less than 90 minutes out.
 *
 * The countdown used to REPLACE the word rather than join it, which is not what
 * the chip is for: the pink marker down the row's left edge and this chip are
 * the two things saying "this is the one that has not been played yet", and
 * dropping the word left that to the marker alone exactly when the row matters
 * most.
 */
function NextChip({ startAt, now }: { startAt: string; now: number }) {
  const diff = new Date(startAt).getTime() - now;
  let label = "NEXT";
  if (diff > 0 && diff < 90 * 60_000) {
    const total = Math.floor(diff / 1000);
    label = `NEXT · IN ${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, "0")}`;
  }
  return <span className="ovl-break-next-chip ovl-race">{label}</span>;
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours()}:${d.getMinutes().toString().padStart(2, "0")}`;
}
